import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Bug, Headset, Lightbulb, Loader2, Send } from 'lucide-react';
import { z } from 'zod';

import { MediaUploadDropzone } from '@/components/shared/media-upload-dropzone';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
    BUG_REPORT_ACCEPT,
    BUG_REPORT_TYPE_META,
    MAX_BUG_REPORT_ATTACHMENTS,
    MAX_BUG_REPORT_ATTACHMENT_SIZE_MB,
} from '../constants';
import { useBugReportAttachmentUpload } from '../hooks/use-bug-report-attachment-upload';
import { useCreateBugReport } from '../hooks/use-bug-reports';
import { BUG_REPORT_TYPES, type BugReportType } from '../types';
import { PendingAttachmentList } from './pending-attachment-list';

const reportBugSchema = z.object({
    type: z.enum(BUG_REPORT_TYPES),
    title: z
        .string()
        .trim()
        .min(1, 'Please give it a short title')
        .max(255, 'Title must be at most 255 characters'),
    description: z
        .string()
        .trim()
        .min(1, 'Please describe it for us')
        .max(10000, 'Description must be at most 10,000 characters'),
});

type ReportBugFormValues = z.infer<typeof reportBugSchema>;

const DEFAULT_VALUES: ReportBugFormValues = { type: 'BUG', title: '', description: '' };

// Support Request is a headset, not a life-buoy: the buoy collapsed into an
// indistinct blob at 14px, and a headset also says "a person you can talk to"
// rather than "emergency", which is what this option actually offers.
//
// The `lifeBuoy` KEY is now a misnomer. It is the string held in
// `BUG_REPORT_TYPE_META[].icon` (see `../constants`), so renaming it is a
// change to that contract, not to this line. Left alone deliberately rather
// than half-renamed here and left inconsistent there.
const TYPE_ICONS = { bug: Bug, lightbulb: Lightbulb, lifeBuoy: Headset } as const;

/** The three-way picker that opens the form. */
function TypePicker({
    value,
    onChange,
}: {
    readonly value: BugReportType;
    readonly onChange: (next: BugReportType) => void;
}) {
    const { t } = useTranslation();

    return (
        <div role="radiogroup" className="grid grid-cols-3 gap-1.5">
            {BUG_REPORT_TYPES.map((type) => {
                const meta = BUG_REPORT_TYPE_META[type];
                const Icon = TYPE_ICONS[meta.icon];
                const isSelected = value === type;

                return (
                    <button
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => onChange(type)}
                        className={cn(
                            'flex flex-col items-start gap-0.5 rounded-md border px-2 py-2 text-left transition-colors focus-visible:ring-3 focus-visible:ring-primary-200 focus-visible:outline-none',
                            isSelected
                                ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/15'
                                : 'border-border-default hover:bg-surface-hover',
                        )}
                    >
                        <span className="flex w-full min-w-0 items-center gap-1">
                            <Icon
                                className={cn(
                                    'size-3.5 shrink-0',
                                    isSelected ? 'text-primary-600' : 'text-text-tertiary',
                                )}
                            />
                            <span className="min-w-0 truncate text-xs leading-tight font-medium text-text-primary">
                                {t(meta.labelKey, meta.label)}
                            </span>
                        </span>
                        <span className="text-2xs leading-snug text-text-tertiary">
                            {t(meta.descriptionKey, meta.description)}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

interface IReportBugFormProps {
    readonly onSubmitted: () => void;
    readonly onCancel: () => void;
}

export function ReportBugForm({ onSubmitted, onCancel }: IReportBugFormProps) {
    const { t } = useTranslation();
    const location = useLocation();

    const attach = useBugReportAttachmentUpload();
    const createReport = useCreateBugReport();

    // No reset effect: ReportBugTrigger remounts the whole dialog on each
    // open (via its session key), so mount state IS the clean state.
    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<ReportBugFormValues>({
        resolver: standardSchemaResolver(reportBugSchema),
        defaultValues: DEFAULT_VALUES,
    });

    // Everything below the picker is worded for the selected type — a bug
    // report, a feature idea and a support question ask for different things.
    // `useWatch` rather than `watch()`: it subscribes to just this field and
    // is the form API React Compiler can memoize.
    const selectedType = useWatch({ control, name: 'type' });
    const copy = BUG_REPORT_TYPE_META[selectedType];

    async function onSubmit(values: ReportBugFormValues) {
        try {
            await createReport.mutateAsync({
                type: values.type,
                title: values.title,
                description: values.description,
                // Where they were when it broke — far more useful than asking them
                // to describe it, and free to capture.
                pageUrl: `${location.pathname}${location.search}`,
                // `uploaded` skips anything still in flight or failed, so a report
                // never claims proof that is not in the bucket.
                attachments: attach.uploaded,
            });
        } catch {
            // `useCreateBugReport`'s `onError` has already toasted this. But
            // `mutateAsync` rejects regardless — that is its contract, unlike
            // `mutate` — and the `void handleSubmit(...)` below discards the
            // promise WITHOUT attaching a rejection handler, so an already-handled
            // failure reached production Sentry as an unhandled crash
            // ("AxiosError: Request failed with status code 503", POST
            // /bug-reports, 15 Sep). Swallow only the mutation's rejection: the
            // toast stands, the dialog stays open and the typed report survives for
            // a retry. Anything thrown below this still propagates.
            return;
        }
        reset(DEFAULT_VALUES);
        attach.clear();
        onSubmitted();
    }

    const isSubmitting = createReport.isPending;

    return (
        <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            className="flex min-h-0 flex-1 flex-col"
        >
            {/* overflow-y-auto is a safety net for short viewports, not the
                normal case — the content below is sized to fit without it. */}
            <DialogBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-3">
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">
                        {t('bugReports.form.type', 'What would you like to report?')}
                    </Label>
                    <Controller
                        control={control}
                        name="type"
                        render={({ field }) => (
                            <TypePicker value={field.value} onChange={field.onChange} />
                        )}
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bug-report-title" className="text-xs" required>
                        {t(copy.titleLabel.key, copy.titleLabel.text)}
                    </Label>
                    {/* `data-size="sm"` drops the value AND its placeholder to
                        13px — the placeholders here are whole example sentences
                        and at the default 14px they overflow the field. */}
                    <Input
                        id="bug-report-title"
                        data-size="sm"
                        placeholder={t(copy.titlePlaceholder.key, copy.titlePlaceholder.text)}
                        {...register('title')}
                    />
                    {errors.title && (
                        <p className="text-xs text-destructive">{errors.title.message}</p>
                    )}
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bug-report-description" className="text-xs" required>
                        {t(copy.detailsLabel.key, copy.detailsLabel.text)}
                    </Label>
                    {/* Textarea has no size variant, so it matches the Input's
                        `sm` type scale by hand. */}
                    <Textarea
                        id="bug-report-description"
                        rows={3}
                        className="text-xs-lg"
                        placeholder={t(copy.detailsPlaceholder.key, copy.detailsPlaceholder.text)}
                        {...register('description')}
                    />
                    {errors.description && (
                        <p className="text-xs text-destructive">{errors.description.message}</p>
                    )}
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">
                        {t(copy.attachmentLabel.key, copy.attachmentLabel.text)}{' '}
                        <span className="font-normal text-text-tertiary">
                            {t('bugReports.form.optional', '(optional)')}
                        </span>
                    </Label>
                    <MediaUploadDropzone
                        density="compact"
                        accept={BUG_REPORT_ACCEPT}
                        disabled={attach.items.length >= MAX_BUG_REPORT_ATTACHMENTS}
                        onFilesSelected={(files) => attach.addFiles(files)}
                        title={t(
                            'bugReports.form.dropzoneTitle',
                            'Drag a file here, or click to browse',
                        )}
                        hint={t(
                            'bugReports.form.dropzoneHint',
                            'Images, PDF, or logs — up to {{size}} MB',
                            { size: MAX_BUG_REPORT_ATTACHMENT_SIZE_MB },
                        )}
                    />

                    {/* The same chip the thread composer stages attachments in
                        — see `pending-attachment-list.tsx`. */}
                    <PendingAttachmentList items={attach.items} onRemove={attach.remove} />
                </div>
            </DialogBody>

            <DialogFooter className="px-5 pb-5">
                <Button type="button" variant="outline" onClick={onCancel}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting || attach.isUploading}>
                    {isSubmitting ? (
                        <Loader2 className="size-4 animate-spin" />
                    ) : (
                        <Send className="size-4" />
                    )}
                    {t(copy.submitLabel.key, copy.submitLabel.text)}
                </Button>
            </DialogFooter>
        </form>
    );
}
